import React, { Dispatch, MutableRefObject, useLayoutEffect, useRef } from 'react'

import { buildMessage, ChatMessage } from '../chatMessage'
import { StreamingAction } from './streamingReducer'

const STOPPED_MESSAGE = 'Stopped.'

export type UseStopProtocolOptions = {
  // Latest in-progress text. Read synchronously by stopGeneration so the
  // fast/slow path decision uses the live value, not a closure snapshot.
  inProgressText: string
  // When non-null, the layout effect commits this as an assistant message
  // before the browser paints — preventing a visible flash between the
  // streaming bubble disappearing and the final message appearing.
  pendingCommit: string | null
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  dispatch: Dispatch<StreamingAction>
  nextId: () => string
}

export type StopProtocol = {
  // Flipped synchronously by stopGeneration and clearConversation so any
  // in-flight DOM step short-circuits before subsequent steps push to chat.
  // Caller threads this to useExecuteDomTools and resets it on every SEND or
  // RESUME so a new turn isn't pre-cancelled by a prior stop.
  cancelledRef: MutableRefObject<boolean>
  stopGeneration: () => void
  clearConversation: () => void
}

/**
 * Owns the stop-generation protocol: the cancelled / stop-pending refs, the
 * layout effect that drains pendingCommit into a chat bubble, and the public
 * stopGeneration handler.
 *
 * The two refs coordinate with the LLM-side reducer: stopGeneration flips
 * cancelledRef synchronously (so DOM-tool chains stop iterating mid-flight),
 * dispatches STOP (which routes any partial inProgressText through
 * pendingCommit), and uses stopPendingRef to tell the layout effect whether
 * a "Stopped." marker should land in the same setMessages call as the
 * partial-text commit. That way the marker always appears directly below
 * the partial text in chat order.
 */
const useStopProtocol = ({
  inProgressText,
  pendingCommit,
  setMessages,
  dispatch,
  nextId
}: UseStopProtocolOptions): StopProtocol => {
  const cancelledRef = useRef(false)
  // Set true by stopGeneration when inProgressText is non-empty so the layout
  // effect that drains pendingCommit can append the "Stopped." bubble in the
  // same setMessages call, guaranteeing partial text lands above the marker.
  const stopPendingRef = useRef(false)
  // Mirror of inProgressText read synchronously by stopGeneration so the fast
  // path cannot fire on a stale closure value. Without this, a TOKEN event
  // committed between the last render and the click would leave stopGeneration
  // seeing '' even though the next render's state has streamed text — taking
  // the wrong branch and landing the "Stopped." bubble before the partial.
  const inProgressTextRef = useRef('')
  inProgressTextRef.current = inProgressText

  // Commit the completed assistant message before the browser paints to avoid
  // a visible flash between inProgressText clearing and the message appearing
  // in the list. When the commit was triggered by stopGeneration, append the
  // "Stopped." bubble in the same setMessages call so it always lands
  // directly below the partial text.
  useLayoutEffect(() => {
    if (pendingCommit === null) return
    const wasStop = stopPendingRef.current
    stopPendingRef.current = false
    // React 18 may invoke functional updaters more than once. Pre-build the
    // assistant (and optional Stopped) messages outside the updater so the
    // counter increments exactly once per effect run, regardless of how many
    // times React replays the updater.
    const commitMessage = buildMessage(nextId(), 'assistant', pendingCommit)
    const stopMessage = wasStop ? buildMessage(nextId(), 'assistant', STOPPED_MESSAGE) : null
    setMessages((prev) =>
      stopMessage ? [...prev, commitMessage, stopMessage] : [...prev, commitMessage]
    )
    dispatch({ type: 'COMMITTED' })
  }, [pendingCommit, setMessages, nextId, dispatch])

  // Order matters: flip cancelledRef BEFORE dispatching STOP so any in-flight
  // DOM step that's about to chain to the next one bails out before the
  // reducer's post-stop guards fire. Then dispatch STOP for the LLM side — it
  // routes any partial inProgressText through pendingCommit so the layout
  // effect appends it as a regular assistant bubble. The "Stopped." append
  // below the partial is sequenced via stopPendingRef which the layout effect
  // picks up after the pendingCommit lands, guaranteeing correct visual
  // order.
  const stopGeneration = (): void => {
    cancelledRef.current = true
    stopPendingRef.current = true
    dispatch({ type: 'STOP' })
    if (inProgressTextRef.current === '') {
      stopPendingRef.current = false
      setMessages((prev) => [...prev, buildMessage(nextId(), 'assistant', STOPPED_MESSAGE)])
    }
  }

  // Ends the turn like stopGeneration, but resets the reducer instead of
  // committing the partial text. Lives here so both endings flip cancelledRef
  // before they dispatch, in the one file that owns the ref.
  const clearConversation = (): void => {
    cancelledRef.current = true
    dispatch({ type: 'CLEAR' })
  }

  return { cancelledRef, stopGeneration, clearConversation }
}

export default useStopProtocol
